// MarketRegimeEngine: Detects regime changes based on BTC/ETH dominance history
import { DominanceHistory } from '../services/macroMarketHistoryService';

export interface MarketRegimeAnalysis {
  currentRegime: 'Bitcoin Dominant' | 'Rotation Altcoins' | 'Rotation ETH' | 'Stable';
  previousRegime: string;
  regimeChanged: boolean;
  changeStrength: 'Faible' | 'Modéré' | 'Fort';
  explanation: string;
}

function getVariation(data: number[]): number {
  if (data.length < 2) return 0;
  return ((data[data.length - 1] - data[0]) / data[0]) * 100;
}

export function detectMarketRegime(history: DominanceHistory, previousRegime: string): MarketRegimeAnalysis {
  const btc = history.data.map(pt => pt.btcDominance);
  const eth = history.data.map(pt => pt.ethDominance);
  const btc7d = getVariation(btc);
  const eth7d = getVariation(eth);
  const btc24h = getVariation(btc.slice(-48)); // assuming 30min interval
  let currentRegime: MarketRegimeAnalysis['currentRegime'] = 'Stable';
  let explanation = '';
  let changeStrength: MarketRegimeAnalysis['changeStrength'] = 'Faible';

  if (btc7d > 3 && btc24h > btc7d / 2) {
    currentRegime = 'Bitcoin Dominant';
    explanation = 'BTC dominance has increased sharply over 7 days with recent acceleration.';
    changeStrength = btc7d > 6 ? 'Fort' : btc7d > 4.5 ? 'Modéré' : 'Faible';
  } else if (btc7d < -3 && eth7d >= 0) {
    currentRegime = 'Rotation Altcoins';
    explanation = 'BTC dominance has decreased while ETH is stable or rising.';
    changeStrength = btc7d < -6 ? 'Fort' : btc7d < -4.5 ? 'Modéré' : 'Faible';
  } else if (eth7d > 2 && Math.abs(btc7d) < 1) {
    currentRegime = 'Rotation ETH';
    explanation = 'ETH dominance has increased significantly while BTC is stable.';
    changeStrength = eth7d > 4 ? 'Fort' : eth7d > 2.5 ? 'Modéré' : 'Faible';
  } else {
    explanation = 'No significant regime change detected.';
  }

  const regimeChanged = currentRegime !== previousRegime && currentRegime !== 'Stable';

  return {
    currentRegime,
    previousRegime,
    regimeChanged,
    changeStrength,
    explanation,
  };
}
